import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plane } from "lucide-react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

// Beta Supabase auth.oauth namespace — local typed wrapper so TS is happy.
type OAuthClient = { name?: string; client_uri?: string; logo_uri?: string };
type AuthorizationDetails = {
  client?: OAuthClient;
  scope?: string;
  redirect_uri?: string;
  redirect_url?: string;
  redirect_to?: string;
};
type OAuthResult<T> = { data: T | null; error: { message: string } | null };
type SupabaseAuthOAuth = {
  getAuthorizationDetails: (id: string) => Promise<OAuthResult<AuthorizationDetails>>;
  approveAuthorization: (id: string) => Promise<OAuthResult<AuthorizationDetails>>;
  denyAuthorization: (id: string) => Promise<OAuthResult<AuthorizationDetails>>;
};
const oauth = (supabase.auth as unknown as { oauth: SupabaseAuthOAuth }).oauth;

const identityScopeLabels: Record<string, string> = {
  openid: "Verify your identity",
  email: "Share your email address",
  profile: "Share your basic profile",
  offline_access: "Stay connected in the background",
};

export default function OAuthConsent() {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") ?? "";
  const [details, setDetails] = useState<AuthorizationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!authorizationId) {
        setError("Missing authorization_id in URL.");
        return;
      }
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        const next = window.location.pathname + window.location.search;
        window.location.href = "/auth?next=" + encodeURIComponent(next);
        return;
      }
      const { data, error: detailsError } = await oauth.getAuthorizationDetails(authorizationId);
      if (!active) return;
      if (detailsError) {
        setError(detailsError.message);
        return;
      }
      const immediate = data?.redirect_url ?? data?.redirect_to;
      if (immediate && !data?.client) {
        window.location.href = immediate;
        return;
      }
      setDetails(data);
    })();
    return () => {
      active = false;
    };
  }, [authorizationId]);

  async function decide(approve: boolean) {
    setBusy(true);
    const { data, error: decisionError } = approve
      ? await oauth.approveAuthorization(authorizationId)
      : await oauth.denyAuthorization(authorizationId);
    if (decisionError) {
      setBusy(false);
      setError(decisionError.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("No redirect returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  const clientName = details?.client?.name ?? "an app";
  const scopes = (details?.scope ?? "").split(/\s+/).filter(Boolean);

  return (
    <main className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-primary/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-6 md:p-8 space-y-6 shadow-lg">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center justify-center p-3 bg-gradient-to-br from-primary to-primary/80 rounded-2xl shadow-md">
            <Plane className="h-7 w-7 text-primary-foreground" aria-hidden />
          </div>
          <h1 className="text-2xl font-bold">Connect {clientName} to Airport Buddy</h1>
          <p className="text-base text-muted-foreground">
            This lets {clientName} use Airport Buddy as you — checking wait times and reading your
            saved airports. It doesn't bypass Airport Buddy's permissions.
          </p>
        </div>

        {error && (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive" role="alert">
            {error}
          </div>
        )}

        {!details && !error && (
          <div className="flex justify-center py-6">
            <LoadingSpinner />
          </div>
        )}

        {details && (
          <>
            {scopes.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-semibold">This app will be able to:</p>
                <ul className="space-y-1.5 text-sm text-muted-foreground">
                  {scopes.map((s) => (
                    <li key={s} className="flex gap-2">
                      <span className="text-primary">•</span>
                      <span>{identityScopeLabels[s] ?? `Additional permission: ${s}`}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <Button
                onClick={() => decide(true)}
                disabled={busy}
                className="min-h-[48px] text-base"
                size="lg"
              >
                {busy ? <LoadingSpinner size="sm" className="mr-2" /> : null}
                Approve
              </Button>
              <Button
                onClick={() => decide(false)}
                disabled={busy}
                variant="outline"
                className="min-h-[48px] text-base"
                size="lg"
              >
                Cancel connection
              </Button>
            </div>
          </>
        )}
      </Card>
    </main>
  );
}
